/**
 * lichiabonsai-data.js — DADOS DO DIÁRIO (edite este arquivo para atualizar)
 *
 * Este é o único arquivo que você precisa mexer para registrar o progresso do
 * bonsai: novas entradas na linha do tempo, novas medições, marcar itens do
 * checklist, etc. É um módulo ES importado pelo script (entra no build do Vite),
 * então: edite → commite → o deploy publica. Não há fetch em runtime.
 *
 * Convenções:
 *  - Textos bilíngues são objetos { pt, it }.
 *  - Datas em ISO "AAAA-MM-DD" (a UI formata conforme o idioma).
 *  - Medições: alturaCm e folhas são NÚMEROS (alimentam o gráfico de crescimento).
 *  - Checklist: done = true quando concluído.
 */

export const META = {
  // Cabeçalho
  titulo: { pt: 'Projeto Bonsai Lichia 2031', it: 'Progetto Bonsai Litchi 2031' },
  subtitulo: {
    pt: 'Diário da transformação de uma semente de lichia em bonsai, em Turate, Lombardia.',
    it: 'Diario della trasformazione di un seme di litchi in bonsai, a Turate, Lombardia.',
  },
  local: { pt: 'Turate · Lombardia, Itália', it: 'Turate · Lombardia, Italia' },
  especie: 'Litchi chinensis',
  dataInicial: '2026-06-21',
  // Foto de destaque (hero). Os arquivos ficam em ./fotos/ (tratados, WebP).
  heroFoto: {
    arquivo: '2026-06-21-broto-macro.webp',
    alt: {
      pt: 'Macro da muda de lichia: caule, restos da semente e primeiras folhas em contraluz.',
      it: 'Macro della piantina di litchi: fusto, resti del seme e prime foglie in controluce.',
    },
  },
};

export const STATUS = {
  // Cada item vira um "card" de status atual
  fase: {
    rotulo: { pt: 'Fase', it: 'Fase' },
    valor: { pt: 'Berçário · crescimento livre', it: 'Vivaio · crescita libera' },
  },
  inicio: {
    rotulo: { pt: 'Início', it: 'Inizio' },
    valor: '2026-06-21', // data ISO → formatada na UI
    isData: true,
  },
  objetivo: {
    rotulo: { pt: 'Objetivo', it: 'Obiettivo' },
    valor: { pt: 'Bonsai ornamental', it: 'Bonsai ornamentale' },
  },
  proximoPasso: {
    rotulo: { pt: 'Próximo passo', it: 'Prossimo passo' },
    valor: { pt: 'Passar o primeiro inverno protegido do frio', it: 'Superare il primo inverno al riparo dal freddo' },
  },
};

export const PLANO_PET = {
  itens: {
    pt: [
      'Até o outono: deixar a muda estabelecer e crescer — incentivar a raiz pivotante e novos lançamentos (ver os cuidados de Verão). Sem podar nem mexer nas raízes.',
      'Antes da 1ª geada (na Lombardia, out/nov): trazer para dentro, sob uma janela bem iluminada ou luz de cultivo LED full-spectrum (~12 h/dia).',
      'No inverno, dentro de casa: longe de correntes frias, rega reduzida e sem adubo; manter alguma umidade (o aquecimento resseca o ar).',
    ],
    it: [
      'Fino all’autunno: lasciar attecchire e crescere la piantina — favorire il fittone e nuovi germogli (vedi cure d’Estate). Senza potare né toccare le radici.',
      'Prima della prima gelata (in Lombardia, ott/nov): portarla in casa, sotto una finestra ben illuminata o una luce LED full-spectrum da coltivazione (~12 h/giorno).',
      'In inverno, in casa: lontano da correnti fredde, irrigazione ridotta e niente concime; mantenere un po’ di umidità (il riscaldamento secca l’aria).',
    ],
  },
  // "Regra de ouro" — destacada como faixa de alerta.
  regra: {
    pt: 'Não cortar a raiz principal (pivotante) durante o primeiro ano — ela engrossa o futuro tronco.',
    it: 'Non tagliare la radice principale (fittone) durante il primo anno — ispessisce il futuro tronco.',
  },
};

// LINHA DO TEMPO — adicione novas entradas no topo (mais recente primeiro).
export const TIMELINE = [
  {
    data: '2026-10-11',
    titulo: { pt: 'Para dentro de casa, com 13 cm e 15 folhas', it: 'In casa, a 13 cm e con 15 foglie' },
    texto: {
      pt: 'Quase dois meses depois do último registro, a muda mede 13 cm — 2 cm a mais que em 22/08 — e soma 15 folhas: dez no tronco principal e cinco no ramo menor. No dia 1º de outubro, com a chegada do outono, ela saiu do terraço e foi trazida para dentro de casa, onde agora é iluminada por uma lâmpada SANSI de 15 W, LED E27 para plantas de interior, a 25 cm da planta e ligada 12 h por dia. É o primeiro passo do plano para o primeiro inverno, antes das geadas da Lombardia. Segue no berçário, na garrafa PET dentro do vaso maior, com a cobertura de fibra de coco, sem poda nem mexida nas raízes.',
      it: 'Quasi due mesi dopo l’ultimo aggiornamento, la piantina misura 13 cm — 2 cm in più rispetto al 22/08 — e conta 15 foglie: dieci sul tronco principale e cinque sul ramo più piccolo. Il 1° ottobre, con l’arrivo dell’autunno, è uscita dal terrazzo ed è stata portata in casa, dove ora è illuminata da una lampadina SANSI da 15 W, LED E27 per piante da interno, a 25 cm dalla pianta e accesa 12 ore al giorno. È il primo passo del piano per il primo inverno, prima delle gelate della Lombardia. Resta nel vivaio, nella bottiglia PET dentro il vaso più grande, con la copertura di fibra di cocco, senza potature né interventi sulle radici.',
    },
    fotos: [
      {
        arquivo: '2026-10-11-vista-do-topo.webp',
        legenda: { pt: 'Vista de cima: a copa mais cheia, com 15 folhas, já dentro de casa', it: 'Vista dall’alto: la chioma più piena, con 15 foglie, ormai in casa' },
      },
      {
        arquivo: '2026-10-11-tronco-e-ramos.webp',
        legenda: { pt: 'Detalhe do tronco principal e do ramo menor saindo da fibra de coco', it: 'Dettaglio del tronco principale e del ramo più piccolo che escono dalla fibra di cocco' },
      },
      {
        arquivo: '2026-10-11-vista-geral.webp',
        legenda: { pt: 'Vista geral, 13 cm de altura, sob a luz da lâmpada LED de 15 W', it: 'Vista generale, 13 cm di altezza, sotto la luce della lampadina LED da 15 W' },
      },
    ],
  },
  {
    data: '2026-08-22',
    titulo: { pt: 'Novo fluxo e 11 cm de altura', it: 'Nuovo getto e 11 cm di altezza' },
    texto: {
      pt: 'Pouco mais de um mês depois do último registro, a muda alcançou 11 cm — 3 cm a mais que na medição de 12/07 — e agora soma 11 folhas. Três delas pertencem ao novo ramo que emerge do solo; as outras oito estão no tronco principal. No ápice, um novo par ainda fino e acobreado amadurece, enquanto o ramo basal exibe seu próprio crescimento verde-claro. A planta segue no berçário, na PET protegida pelo vaso maior, sem poda nem intervenção nas raízes.',
      it: 'Poco più di un mese dopo l’ultimo aggiornamento, la piantina ha raggiunto 11 cm — 3 cm in più rispetto alla misurazione del 12/07 — e ora conta 11 foglie. Tre appartengono al nuovo ramo che emerge dal terreno; le altre otto si trovano sul tronco principale. All’apice, una nuova coppia ancora sottile e color rame sta maturando, mentre il ramo basale mostra la propria crescita verde chiaro. La pianta rimane nel vivaio, nella bottiglia PET protetta dal vaso più grande, senza potature né interventi sulle radici.',
    },
    fotos: [
      {
        arquivo: '2026-08-22-vista-geral.webp',
        legenda: { pt: 'Vista lateral: 11 cm de altura, com o tronco principal e o novo ramo que emerge do solo', it: 'Vista laterale: 11 cm di altezza, con il tronco principale e il nuovo ramo che emerge dal terreno' },
      },
      {
        arquivo: '2026-08-22-vista-do-topo.webp',
        legenda: { pt: 'Vista de cima das 11 folhas: oito no tronco principal e três no novo ramo', it: 'Vista dall’alto delle 11 foglie: otto sul tronco principale e tre sul nuovo ramo' },
      },
      {
        arquivo: '2026-08-22-brotacoes-novas.webp',
        legenda: { pt: 'Detalhe do novo par acobreado no tronco principal e do crescimento verde-claro do ramo basal', it: 'Dettaglio della nuova coppia color rame sul tronco principale e della crescita verde chiaro del ramo basale' },
      },
    ],
  },
  {
    data: '2026-07-18',
    titulo: { pt: 'O bronze virou verde', it: 'Il bronzo è diventato verde' },
    texto: {
      pt: 'Seis dias depois, o lançamento de julho completou a virada de cor: o par de folhas que nasceu acobreado agora está verde — ainda num verde-limão claro, mais tenro que o verde-escuro das duas folhas originais, mas já sem nenhum resquício de vermelho. É a confirmação de que aquele broto pegou: virou tecido maduro e permanente, não um susto passageiro. A muda segue firme, sem manchas nem sinais de estresse, e o ramo novo sustenta quatro folhas bem abertas.',
      it: 'Sei giorni dopo, il getto di luglio ha completato il cambio di colore: la coppia di foglie nata color rame è ora verde — ancora un verde lime chiaro, più tenero del verde scuro delle due foglie originali, ma senza più alcuna traccia di rosso. È la conferma che quel germoglio ha attecchito: è diventato tessuto maturo e permanente, non uno spavento passeggero. La piantina resta salda, senza macchie né segni di stress, e il nuovo ramo sorregge quattro foglie ben aperte.',
    },
    fotos: [
      {
        arquivo: '2026-07-18-ramo-verde.webp',
        legenda: { pt: 'O ramo novo já sem vermelho: o par jovem verde-limão ao lado das folhas maduras verde-escuras', it: 'Il nuovo ramo senza più rosso: la coppia giovane verde lime accanto alle foglie mature verde scuro' },
      },
      {
        arquivo: '2026-07-18-vista-geral.webp',
        legenda: { pt: 'Vista de cima: as quatro folhas do lançamento, todas verdes, ainda dentro da garrafa PET', it: 'Vista dall’alto: le quattro foglie del getto, tutte verdi, ancora nella bottiglia PET' },
      },
      {
        arquivo: '2026-07-18-estacao-cultivo.webp',
        legenda: { pt: 'A estação de cultivo no terraço de Turate: grade, ar-condicionado e o vaso-berçário', it: 'La postazione di coltivazione sul terrazzo di Turate: ringhiera, condizionatore e il vaso-vivaio' },
      },
    ],
  },
  {
    data: '2026-07-14',
    titulo: { pt: 'As folhas jovens começam a firmar', it: 'Le foglie giovani iniziano a rassodarsi' },
    texto: {
      pt: 'Dois dias depois de abrir, o par de folhas novas já perde o vermelho intenso e clareia para um bronze-bege — o primeiro passo da maturação. Aparece também uma gema apical verde apontando para cima: sinal de que o ramo não parou, continua se estendendo. Bonito contraste na mesma muda: as duas folhas originais verde-escuras firmes, e o lançamento novo ainda tenro, a caminho do verde.',
      it: 'Due giorni dopo l’apertura, la coppia di foglie nuove perde già il rosso intenso e schiarisce verso un bronzo-beige — il primo passo della maturazione. Compare anche una gemma apicale verde rivolta verso l’alto: segno che il ramo non si è fermato, continua ad allungarsi. Bel contrasto sulla stessa piantina: le due foglie originali verde scuro salde, e il getto nuovo ancora tenero, in cammino verso il verde.',
    },
    fotos: [
      {
        arquivo: '2026-07-14-bronze-endurece.webp',
        legenda: { pt: 'As folhas novas clareando de vermelho para bronze-bege, ainda caídas e tenras', it: 'Le foglie nuove che schiariscono dal rosso al bronzo-beige, ancora ricadenti e tenere' },
      },
      {
        arquivo: '2026-07-14-vista-geral.webp',
        legenda: { pt: 'Vista de cima: folhas originais verde-escuras e o lançamento novo em bronze claro', it: 'Vista dall’alto: foglie originali verde scuro e il getto nuovo in bronzo chiaro' },
      },
      {
        arquivo: '2026-07-14-estacao-cultivo.webp',
        legenda: { pt: 'O berçário no terraço: a garrafa PET com fibra de coco encaixada no vaso de terra', it: 'Il vivaio sul terrazzo: la bottiglia PET con fibra di cocco incassata nel vaso di terra' },
      },
    ],
  },
  {
    data: '2026-07-12',
    titulo: { pt: 'Novo fluxo abre duas folhas', it: 'Il nuovo getto apre due foglie' },
    texto: {
      pt: 'O broto que apareceu em 04/07 alongou em apenas oito dias e abriu seu primeiro fluxo: duas folhas novas, ainda finas e acobreadas, acima das duas folhas verdes originais. A muda chegou a 8 cm e agora soma quatro folhas. A cor bronze e a textura caída são normais nesta fase jovem; elas devem firmar e ficar verdes conforme amadurecem.',
      it: 'Il germoglio comparso il 04/07 si è allungato in appena otto giorni e ha aperto il suo primo getto: due foglie nuove, ancora sottili e color rame, sopra le due foglie verdi originali. La piantina ha raggiunto 8 cm e ora conta quattro foglie. Il colore bronzo e l’aspetto ricadente sono normali in questa fase giovane; le foglie dovrebbero rassodarsi e diventare verdi con la maturazione.',
    },
    fotos: [
      {
        arquivo: '2026-07-12-altura-8-cm.webp',
        legenda: { pt: 'A régua confirma 8 cm da base ao topo do novo fluxo', it: 'Il righello conferma 8 cm dalla base alla cima del nuovo getto' },
      },
      {
        arquivo: '2026-07-12-ramo-folhas-bronze.webp',
        legenda: { pt: 'Vista geral simétrica: as duas folhas verdes maduras seguram as duas novas, ainda acobreadas, no ápice', it: 'Vista generale simmetrica: le due foglie verdi mature sorreggono le due nuove, ancora ramate, all’apice' },
      },
      {
        arquivo: '2026-07-12-folhas-acobreadas.webp',
        legenda: { pt: 'Detalhe das duas folhas jovens, ainda acobreadas e delicadas', it: 'Dettaglio delle due foglie giovani, ancora ramate e delicate' },
      },
    ],
  },
  {
    data: '2026-07-04',
    titulo: {
      pt: 'Primeiro ramo novo',
      it: 'Primo nuovo ramo',
    },
    texto: {
      pt: 'Treze dias depois da germinação, a muda deu seu primeiro sinal claro de que decidiu crescer, não só sobreviver: no nó entre as duas folhas originais, surgiu um broto verde-amarelado se desenrolando aos poucos — o começo de um ramo novo. As duas folhas de sempre continuam firmes e verdes, mas agora dividem a cena com esse fio de crescimento que ainda não existia. É pouco pra notar no dia a dia, mas é exatamente o que a fase de berçário está tentando garantir.',
      it: 'Tredici giorni dopo la germinazione, la piantina ha dato il primo segnale chiaro di aver deciso di crescere, non solo di sopravvivere: nel nodo tra le due foglie originali è spuntato un germoglio verde-giallastro che si sta lentamente aprendo — l\'inizio di un nuovo ramo. Le due foglie di sempre restano salde e verdi, ma ora condividono la scena con questo filo di crescita che prima non c\'era. È un dettaglio minimo da notare giorno per giorno, ma è esattamente ciò che la fase di vivaio cerca di ottenere.',
    },
    fotos: [
      {
        arquivo: '2026-07-04-broto-novo.webp',
        legenda: { pt: 'O broto verde-amarelado se desenrolando no nó entre as duas folhas — o primeiro ramo novo da muda', it: 'Il germoglio verde-giallastro che si apre nel nodo tra le due foglie — il primo nuovo ramo della piantina' },
      },
      {
        arquivo: '2026-07-04-duas-folhas.webp',
        legenda: { pt: 'As duas folhas de sempre, firmes e verdes — o "antes" desta leva de fotos', it: 'Le due foglie di sempre, salde e verdi — il "prima" di questa serie di foto' },
      },
      {
        arquivo: '2026-07-04-vaso-ar-condicionado.webp',
        legenda: { pt: 'O vaso na estação de cultivo, com a unidade externa do ar-condicionado ao fundo — a fonte da água de rega', it: 'Il vaso nella postazione di coltivazione, con l\'unità esterna del condizionatore sullo sfondo — la fonte dell\'acqua per l\'irrigazione' },
      },
    ],
  },
  {
    data: '2026-06-26',
    titulo: {
      pt: 'Vaso isolante e rega com água do ar-condicionado',
      it: 'Vaso isolante e irrigazione con acqua del condizionatore',
    },
    texto: {
      pt: 'A garrafa PET, furada no fundo, foi encaixada dentro de um vaso maior cheio de terra — isolando as raízes do calor e mantendo-as no escuro. A base do troncozinho ganhou uma cobertura de casca de coco; o resto, uma mistura fofa de fibra de coco e terra preta. A rega passou a ser com a água de condensação do ar-condicionado: naturalmente macia e levemente ácida, ótima para a lichia (evita o calcário que amarela as folhas novas).',
      it: 'La bottiglia PET, forata sul fondo, è stata inserita in un vaso più grande pieno di terra — isolando le radici dal calore e tenendole al buio. La base del piccolo tronco ha ricevuto una copertura di fibra di cocco; il resto, una miscela soffice di fibra di cocco e terra nera. L’irrigazione ora usa l’acqua di condensa del condizionatore: naturalmente dolce e leggermente acida, ottima per il litchi (evita il calcare che ingiallisce le foglie nuove).',
    },
    fotos: [
      {
        arquivo: '2026-06-26-vaso-isolante-detalhe.webp',
        legenda: { pt: 'A garrafa encaixada no vaso maior, com cobertura de casca de coco', it: 'La bottiglia inserita nel vaso più grande, con copertura di fibra di cocco' },
      },
      {
        arquivo: '2026-06-26-muda.webp',
        legenda: { pt: 'A muda, com novas folhas firmes e verdes', it: 'La piantina, con nuove foglie sode e verdi' },
      },
      {
        arquivo: '2026-06-26-vaso-isolante.webp',
        legenda: { pt: 'O conjunto: vaso dentro do vaso, no terraço em Turate', it: 'L’insieme: vaso dentro il vaso, sul terrazzo a Turate' },
      },
    ],
  },
  {
    data: '2026-06-25',
    titulo: {
      pt: 'Transplante para a garrafa PET de 2 L',
      it: 'Trapianto nella bottiglia PET da 2 L',
    },
    texto: {
      pt: 'Mudança para uma garrafa PET de 2 litros cortada, cheia de substrato de fibra de coco — espaço em altura para a raiz pivotante crescer reta. As folhas já estão maiores e mais verdes; a raiz principal foi preservada.',
      it: 'Spostata in una bottiglia PET da 2 litri tagliata, riempita di substrato in fibra di cocco — spazio in altezza per far crescere dritto il fittone. Le foglie sono più grandi e verdi; la radice principale è stata preservata.',
    },
    fotos: [
      {
        arquivo: '2026-06-25-transplante-detalhe.webp',
        legenda: { pt: 'Folhas maiores e a boca cortada da garrafa', it: 'Foglie più grandi e l’imboccatura tagliata della bottiglia' },
      },
      {
        arquivo: '2026-06-25-transplante-topo.webp',
        legenda: { pt: 'Vista de cima: as folhas no substrato de fibra de coco', it: 'Vista dall’alto: le foglie nel substrato di fibra di cocco' },
      },
      {
        arquivo: '2026-06-25-transplante-janela.webp',
        legenda: { pt: 'A garrafa de 2 L no peitoril, em Turate', it: 'La bottiglia da 2 L sul davanzale, a Turate' },
      },
    ],
  },
  {
    data: '2026-06-21',
    titulo: { pt: 'Primeira muda germinada', it: 'Prima piantina germinata' },
    texto: {
      pt: 'A semente brotou. Raiz já próxima ao fundo do vaso de germinação. Duas primeiras folhas abertas.',
      it: 'Il seme è germogliato. Radice già vicina al fondo del vaso di germinazione. Prime due foglie aperte.',
    },
    // Galeria desta entrada (arquivos em ./fotos/). Adicione fotos novas aqui.
    fotos: [
      {
        arquivo: '2026-06-21-broto-topo.webp',
        legenda: { pt: 'Vista de cima — as duas primeiras folhas', it: 'Vista dall’alto — le prime due foglie' },
      },
      {
        arquivo: '2026-06-21-jardineira.webp',
        legenda: { pt: 'A jardineira no peitoril, em Turate', it: 'La fioriera sul davanzale, a Turate' },
      },
      {
        arquivo: '2026-06-21-broto-perfil.webp',
        legenda: { pt: 'Perfil da muda', it: 'Profilo della piantina' },
      },
    ],
  },
];

// METAS por ano (do mais próximo ao mais distante).
export const METAS = [
  { ano: '2026', texto: { pt: 'Sobreviver ao transplante para o PET 2 L', it: 'Sopravvivere al trapianto nel PET 2 L' } },
  { ano: '2027', texto: { pt: 'Engrossar o tronco', it: 'Ispessire il tronco' } },
  { ano: '2028', texto: { pt: 'Primeira poda estrutural', it: 'Prima potatura strutturale' } },
  { ano: '2029', texto: { pt: 'Vaso de treinamento', it: 'Vaso di allenamento' } },
  { ano: '2031+', texto: { pt: 'Bonsai inicial formado', it: 'Bonsai iniziale formato' } },
];

// CHECKLIST de progresso (ordem cronológica). done = true quando concluído.
export const CHECKLIST = [
  { label: { pt: 'Germinação', it: 'Germinazione' }, done: true },
  { label: { pt: 'Transplante PET 2 L', it: 'Trapianto PET 2 L' }, done: true },
  { label: { pt: 'Primeiro inverno', it: 'Primo inverno' }, done: false },
  { label: { pt: 'Primeira poda', it: 'Prima potatura' }, done: false },
  { label: { pt: 'Bonsai inicial', it: 'Bonsai iniziale' }, done: false },
];

// MEDIÇÕES — alturaCm e folhas são números (gráfico de crescimento).
export const MEDICOES = [
  { data: '2026-06-21', alturaCm: 5, folhas: 2 },
  { data: '2026-06-25', alturaCm: 7, folhas: 2 },
  { data: '2026-07-04', alturaCm: 7, folhas: 2 },
  { data: '2026-07-12', alturaCm: 8, folhas: 4 },
  { data: '2026-08-22', alturaCm: 11, folhas: 11 },
  { data: '2026-10-11', alturaCm: 13, folhas: 15 },
];

// REGISTROS POR FASE — SUAS fotos/notas reais de cada fase do guia (o loop de
// feedback do projeto). Anexe aqui a evolução real à fase correspondente do
// roteiro: a UI mostra o bloco "Seu registro" dentro de cada fase do guia.
// Chaves = chave da fase em `guia-fases.js` (f0, f1, f2, f3, f4, f5).
// Fotos: ponha o .webp em ./fotos/ e referencie o nome do arquivo aqui.
export const FASE_REGISTROS = {
  f0: {
    nota: {
      pt: 'Germinação em 21/06, transplante para a garrafa PET de 2 L em 25/06 e, em 26/06, a garrafa (furada no fundo) foi para dentro de um vaso maior com terra — isolando as raízes do calor e da luz. Rega com água de condensação do ar-condicionado (macia, levemente ácida). A raiz pivotante foi preservada. Em 04/07 surgiu o primeiro broto no nó entre as duas folhas; em 12/07 ele já havia alongado, aberto duas folhas novas e levado a muda a 8 cm e quatro folhas. Entre 14/07 e 18/07 essas folhas novas perderam o vermelho de brotação e amadureceram para verde — o lançamento firmou. Em 22/08, a muda chegou a 11 cm e 11 folhas: três no novo ramo que emerge do solo e oito no tronco principal, incluindo um novo par acobreado no ápice. Levada para dentro de casa em 01/10, chegou em 11/10 — sob uma lâmpada LED de 15 W — a 13 cm e 15 folhas: dez no tronco principal e cinco no ramo menor.',
      it: 'Germinazione il 21/06, trapianto nella bottiglia PET da 2 L il 25/06 e, il 26/06, la bottiglia (forata sul fondo) è finita dentro un vaso più grande con terra — isolando le radici dal calore e dalla luce. Irrigazione con acqua di condensa del condizionatore (dolce, leggermente acida). Il fittone è stato preservato. Il 04/07 è comparso il primo germoglio nel nodo tra le due foglie; il 12/07 si era già allungato, aveva aperto due foglie nuove e portato la piantina a 8 cm e quattro foglie. Tra il 14/07 e il 18/07 quelle foglie nuove hanno perso il rosso di germogliazione e sono maturate al verde — il getto si è consolidato. Il 22/08 la piantina ha raggiunto 11 cm e 11 foglie: tre sul nuovo ramo che emerge dal terreno e otto sul tronco principale, compresa una nuova coppia color rame all’apice. Portata in casa il 01/10, l’11/10 — sotto una lampadina LED da 15 W — ha raggiunto 13 cm e 15 foglie: dieci sul tronco principale e cinque sul ramo più piccolo.',
    },
    fotos: [
      { arquivo: '2026-06-26-vaso-isolante-detalhe.webp', legenda: { pt: 'O vaso isolante: garrafa furada dentro de um vaso com terra', it: 'Il vaso isolante: bottiglia forata dentro un vaso con terra' } },
      { arquivo: '2026-06-21-broto-macro.webp', legenda: { pt: 'A muda recém-germinada', it: 'La piantina appena germinata' } },
      { arquivo: '2026-07-04-broto-novo.webp', legenda: { pt: 'O primeiro broto de um ramo novo, no nó entre as duas folhas', it: 'Il primo germoglio di un nuovo ramo, nel nodo tra le due foglie' } },
      { arquivo: '2026-07-12-folhas-acobreadas.webp', legenda: { pt: 'O primeiro fluxo com duas folhas novas', it: 'Il primo getto con due foglie nuove' } },
      { arquivo: '2026-07-18-ramo-verde.webp', legenda: { pt: 'Seis dias depois: as folhas novas já verdes, o lançamento firmado', it: 'Sei giorni dopo: le foglie nuove ormai verdi, il getto consolidato' } },
      { arquivo: '2026-08-22-vista-geral.webp', legenda: { pt: 'A muda com 11 cm e a copa mais cheia', it: 'La piantina a 11 cm con la chioma più piena' } },
      { arquivo: '2026-08-22-brotacoes-novas.webp', legenda: { pt: 'Novo par acobreado no tronco principal e crescimento verde-claro no ramo basal', it: 'Nuova coppia color rame sul tronco principale e crescita verde chiaro sul ramo basale' } },
    ],
  },
  // f1: { nota: { pt: '', it: '' }, fotos: [{ arquivo: 'AAAA-MM-DD-....webp', legenda: { pt: '', it: '' } }] },
};

// ESTAÇÃO DE CULTIVO — próximos passos de INFRAESTRUTURA (vaso, irrigação, luz),
// separados da progressão do bonsai. São planos a executar; edite conforme montar.
export const ESTACAO_CULTIVO = [
  {
    chave: 'vaso',
    rotulo: { pt: 'Vaso', it: 'Vaso' },
    resumo: { pt: 'Fundo e isolado, para a raiz pivotante', it: 'Profondo e isolato, per il fittone' },
    itens: {
      pt: [
        'Profundo: 30–40 cm de altura, ~15–18 cm de largura (profundo > largo).',
        'Isopor 2–3 cm laminado com fibra de vidro (dentro e fora); resina bem curada.',
        'Forro interno inerte (manta de lago), cor clara e ótima drenagem no fundo.',
        'Transplantar só na primavera de 2027 — não mexer na raiz no 1º ano.',
      ],
      it: [
        'Profondo: 30–40 cm di altezza, ~15–18 cm di larghezza (profondo > largo).',
        'Polistirolo 2–3 cm laminato con fibra di vetro (dentro e fuori); resina ben indurita.',
        'Rivestimento interno inerte (telo da laghetto), colore chiaro e ottimo drenaggio sul fondo.',
        'Trapiantare solo in primavera 2027 — non toccare la radice nel 1° anno.',
      ],
    },
  },
  {
    chave: 'irrigacao',
    rotulo: { pt: 'Irrigação', it: 'Irrigazione' },
    resumo: { pt: 'Automática, por umidade', it: 'Automatica, a umidità' },
    itens: {
      pt: [
        'Reservatório + bomba pequena (12 V/USB) com gotejamento no topo.',
        'Controle por sensor de umidade (ESP32): rega só quando seca — evita apodrecer a raiz.',
        'Pulsos curtos, deixando secar um pouco entre regas. Água da chuva ou levemente ácida.',
      ],
      it: [
        'Serbatoio + pompetta (12 V/USB) con gocciolatoio in alto.',
        'Controllo a sensore di umidità (ESP32): irriga solo quando asciuga — evita il marciume radicale.',
        'Impulsi brevi, lasciando asciugare un po’ tra le irrigazioni. Acqua piovana o leggermente acida.',
      ],
    },
  },
  {
    chave: 'luz',
    rotulo: { pt: 'Iluminação', it: 'Illuminazione' },
    resumo: { pt: 'LED de cultivo no timer', it: 'LED da coltivazione a timer' },
    itens: {
      pt: [
        'LED full-spectrum branco (~20–40 W), braço ajustável, ~20–30 cm acima.',
        'Em uso desde 01/10/2026: lâmpada SANSI 15 W, LED E27 para plantas de interior, a 25 cm da planta, 12 h/dia (provisória, até o LED full-spectrum com braço).',
        'Timer ou smart plug: 12–14 h/dia no inverno, sempre no mesmo horário.',
        'Pode ser controlada pelo mesmo ESP32 (luz + bomba juntas).',
      ],
      it: [
        'LED full-spectrum bianco (~20–40 W), braccio regolabile, ~20–30 cm sopra.',
        'In uso dal 01/10/2026: lampadina SANSI 15 W, LED E27 per piante da interno, a 25 cm dalla pianta, 12 ore/giorno (provvisoria, in attesa del LED full-spectrum con braccio).',
        'Timer o smart plug: 12–14 h/giorno in inverno, sempre alla stessa ora.',
        'Gestibile dallo stesso ESP32 (luce + pompa insieme).',
      ],
    },
  },
];
